import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useOrgContext } from "@components/layout/OrgContext";

function billingAuthority(membership: { role: string; status: string } | null | undefined) {
  return membership?.status === "active" && ["owner", "admin"].includes(membership.role);
}

function readBillingTarget() {
  const values = new URLSearchParams(window.location.search).getAll("org");
  if (values.length === 0) return { id: null, error: null };
  const parsed = z.string().uuid().safeParse(values[0]);
  if (values.length !== 1 || !parsed.success) return { id: null, error: "This billing link has an invalid workspace." };
  return { id: parsed.data.toLowerCase(), error: null };
}

/** Resolve reminder links before any billing request; never fall back to another workspace. */
export function useBillingWorkspace() {
  const { currentOrg, currentMembership, orgs, hasLoaded, isLoading, isSwitching, error: contextError, switchOrg } = useOrgContext();
  const [target] = useState(readBillingTarget);
  const [resolved, setResolved] = useState(!target.id && !target.error);
  const [resolutionError, setResolutionError] = useState<string | null>(target.error);
  const [switchSucceeded, setSwitchSucceeded] = useState(false);
  const attemptedSwitch = useRef(false);
  const mounted = useRef(true);
  const targetMembership = orgs.find((entry) => entry.org.id === target.id);
  const targetAllowed = billingAuthority(targetMembership?.membership);
  const currentAllowed = currentMembership?.orgId === currentOrg?.id && billingAuthority(currentMembership);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (resolved || resolutionError || !target.id || !hasLoaded || isLoading) return;
    if (!targetAllowed) {
      setResolutionError("You do not have owner or administrator access to the workspace in this billing link.");
      return;
    }
    if (isSwitching) return;
    if (attemptedSwitch.current && !switchSucceeded) return;
    if (currentOrg?.id === target.id && currentAllowed) {
      const url = new URL(window.location.href);
      url.searchParams.delete("org");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      setResolved(true);
      return;
    }
    if (switchSucceeded) {
      setResolutionError("The workspace in this billing link could not be opened. No billing action was taken.");
      return;
    }
    attemptedSwitch.current = true;
    void switchOrg(target.id, { silent: true }).then(() => {
      if (mounted.current) setSwitchSucceeded(true);
    }).catch(() => {
      if (mounted.current) setResolutionError("Unable to open the workspace in this billing link. No billing action was taken. Try the link again.");
    });
  }, [resolved, resolutionError, target.id, hasLoaded, isLoading, targetAllowed, isSwitching, switchSucceeded, currentOrg?.id, currentAllowed, switchOrg]);

  let error = resolutionError ?? contextError;
  if (!error && resolved && hasLoaded && !isLoading && !currentAllowed) {
    error = "You need owner or administrator access to manage billing for this workspace.";
  }
  const ready = resolved && !error && hasLoaded && !isLoading && !isSwitching && currentAllowed;
  return { ready, error, orgId: currentOrg?.id ?? null, workspaceName: currentOrg?.name ?? "your workspace" };
}
